import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Plus, Pencil, Trash2, SlidersHorizontal, X, Save, Image } from 'lucide-react';
import { toast } from 'sonner';
import FileUploader from '../../components/admin/FileUploader';
import LanguageTabs, { useAvailableLanguages } from '../../components/admin/LanguageTabs';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminSlider = () => {
  const { getAuthHeader } = useAdminAuth();
  const { languages, loading: languagesLoading } = useAvailableLanguages();
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSlider, setEditingSlider] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('pl');

  // Initialize empty slider with all available languages
  const getEmptySlider = () => {
    const titleObj = {};
    const subtitleObj = {};
    const buttonTextObj = {};
    languages.forEach(lang => {
      titleObj[lang.code] = '';
      subtitleObj[lang.code] = '';
      buttonTextObj[lang.code] = '';
    });
    return {
      title: titleObj,
      subtitle: subtitleObj,
      buttonText: buttonTextObj,
      buttonUrl: '',
      buttonOpenInNewTab: false,
      imageUrl: '',
      backgroundColor: '#8b5cf6',
      scheduledFrom: '',
      scheduledTo: '',
      sortOrder: 0,
      isActive: true
    };
  };

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/slider?all=true`, {
        headers: getAuthHeader()
      });
      setSliders(response.data);
    } catch (error) {
      console.error('Failed to fetch sliders:', error);
      toast.error('Nie udało się pobrać danych');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        await axios.post(`${API}/api/admin/slider`, editingSlider, {
          headers: getAuthHeader()
        });
        toast.success('Slider utworzony');
      } else {
        await axios.put(`${API}/api/admin/slider/${editingSlider.slider_id}`, editingSlider, {
          headers: getAuthHeader()
        });
        toast.success('Slider zaktualizowany');
      }
      setEditingSlider(null);
      setIsCreating(false);
      fetchSliders();
    } catch (error) {
      toast.error('Nie udało się zapisać');
    }
  };

  const handleDelete = async (sliderId) => {
    if (!confirm('Czy na pewno chcesz usunąć ten element?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/slider/${sliderId}`, {
        headers: getAuthHeader()
      });
      toast.success('Slider usunięty');
      fetchSliders();
    } catch (error) {
      toast.error('Nie udało się usunąć');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Zarządzanie Sliderem</h1>
          <p className="text-gray-400">Karuzela promocyjna pod Hero</p>
        </div>
        <Button
          onClick={() => { setEditingSlider(getEmptySlider()); setIsCreating(true); setActiveTab('pl'); }}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Dodaj Slide
        </Button>
      </div>

      {/* Slider List */}
      <div className="grid gap-4">
        {sliders.map((slider) => (
          <Card key={slider.slider_id} className="bg-white/5 border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div 
                  className="w-32 h-20 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: slider.backgroundColor || '#8b5cf6' }}
                >
                  <SlidersHorizontal className="w-8 h-8 text-white/50" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-medium truncate">
                      {slider.title?.pl || slider.title?.en || 'Bez tytułu'}
                    </h3>
                    {!slider.isActive && (
                      <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">
                        Nieaktywny
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm truncate">
                    {slider.subtitle?.pl || slider.subtitle?.en || 'Brak opisu'}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    Kolejność: {slider.sortOrder || 0}
                    {slider.buttonUrl && ` | Link: ${slider.buttonUrl}`}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => { setEditingSlider(slider); setIsCreating(false); }}
                    className="text-gray-400 hover:text-white"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(slider.slider_id)}
                    className="text-gray-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {sliders.length === 0 && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-8 text-center">
              <SlidersHorizontal className="w-12 h-12 mx-auto text-gray-500 mb-4" />
              <p className="text-gray-400">Brak sliderów</p>
              <Button
                onClick={() => { setEditingSlider(getEmptySlider()); setIsCreating(true); setActiveTab('pl'); }}
                variant="outline"
                className="mt-4"
              >
                Dodaj pierwszy slider
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Modal */}
      {editingSlider && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-gray-900 z-10">
              <CardTitle className="text-white">
                {isCreating ? 'Nowy Slider' : 'Edytuj Slider'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setEditingSlider(null); setIsCreating(false); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label className="text-gray-300">Obraz tła (opcjonalnie)</Label>
                <FileUploader
                  accept="image/*"
                  currentUrl={editingSlider.imageUrl}
                  onUpload={(url) => setEditingSlider({ ...editingSlider, imageUrl: url })}
                />
                <p className="text-gray-500 text-xs">lub wklej URL:</p>
                <Input
                  value={editingSlider.imageUrl || ''}
                  onChange={(e) => setEditingSlider({ ...editingSlider, imageUrl: e.target.value })}
                  placeholder="https://..."
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Language Tabs for Title/Subtitle/Button Text */}
              <div className="space-y-4">
                <LanguageTabs
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  showIndicator={true}
                  hasContent={languages.reduce((acc, lang) => {
                    acc[lang.code] = !!(editingSlider.title?.[lang.code] || editingSlider.subtitle?.[lang.code] || editingSlider.buttonText?.[lang.code]);
                    return acc;
                  }, {})}
                />

                {languages.map(lang => (
                  activeTab === lang.code && (
                    <div key={lang.code} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label className="text-gray-300">Tytuł ({lang.nativeName})</Label>
                        <Input
                          value={editingSlider.title?.[lang.code] || ''}
                          onChange={(e) => setEditingSlider({ 
                            ...editingSlider, 
                            title: { ...editingSlider.title, [lang.code]: e.target.value } 
                          })}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300">Podtytuł ({lang.nativeName})</Label>
                        <Input
                          value={editingSlider.subtitle?.[lang.code] || ''}
                          onChange={(e) => setEditingSlider({ 
                            ...editingSlider, 
                            subtitle: { ...editingSlider.subtitle, [lang.code]: e.target.value } 
                          })}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300">Tekst przycisku ({lang.nativeName})</Label>
                        <Input
                          value={editingSlider.buttonText?.[lang.code] || ''}
                          onChange={(e) => setEditingSlider({ 
                            ...editingSlider, 
                            buttonText: { ...editingSlider.buttonText, [lang.code]: e.target.value } 
                          })}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                    </div>
                  )
                ))}
              </div>

              {/* Button URL */}
              <div className="space-y-2">
                <Label className="text-gray-300">URL przycisku</Label>
                <div className="flex items-center gap-3">
                  <Input
                    value={editingSlider.buttonUrl || ''}
                    onChange={(e) => setEditingSlider({ ...editingSlider, buttonUrl: e.target.value })}
                    placeholder="https://..."
                    className="bg-white/5 border-white/10 text-white flex-1"
                  />
                  <label className="flex items-center gap-2 text-gray-300 text-sm whitespace-nowrap cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingSlider.buttonOpenInNewTab || false}
                      onChange={(e) => setEditingSlider({ ...editingSlider, buttonOpenInNewTab: e.target.checked })}
                      className="rounded border-white/20 bg-white/5 text-purple-600 focus:ring-purple-500"
                    />
                    Nowe okno
                  </label>
                </div>
              </div>

              {/* Background Color */}
              <div className="space-y-2">
                <Label className="text-gray-300">Kolor tła</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={editingSlider.backgroundColor || '#8b5cf6'}
                    onChange={(e) => setEditingSlider({ ...editingSlider, backgroundColor: e.target.value })}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={editingSlider.backgroundColor || '#8b5cf6'}
                    onChange={(e) => setEditingSlider({ ...editingSlider, backgroundColor: e.target.value })}
                    className="bg-white/5 border-white/10 text-white flex-1"
                  />
                </div>
              </div>

              {/* Scheduling */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Widoczny od</Label>
                  <Input
                    type="datetime-local"
                    value={editingSlider.scheduledFrom ? editingSlider.scheduledFrom.slice(0, 16) : ''}
                    onChange={(e) => setEditingSlider({ ...editingSlider, scheduledFrom: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Widoczny do</Label>
                  <Input
                    type="datetime-local"
                    value={editingSlider.scheduledTo ? editingSlider.scheduledTo.slice(0, 16) : ''}
                    onChange={(e) => setEditingSlider({ ...editingSlider, scheduledTo: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Sort Order & Active */}
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Label className="text-gray-300">Kolejność</Label>
                  <Input
                    type="number"
                    value={editingSlider.sortOrder || 0}
                    onChange={(e) => setEditingSlider({ ...editingSlider, sortOrder: parseInt(e.target.value) })}
                    className="bg-white/5 border-white/10 text-white w-24"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-gray-300">Aktywny</Label>
                  <Switch
                    checked={editingSlider.isActive}
                    onCheckedChange={(checked) => setEditingSlider({ ...editingSlider, isActive: checked })}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setEditingSlider(null); setIsCreating(false); }}>
                  Anuluj
                </Button>
                <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" />
                  Zapisz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminSlider;
